import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import type { Entry, Fixture, Round } from '@h2h/shared';
import { SCREEN_COLUMN } from '../../components/ui/AppShell';
import { cn } from '../../lib/cn';
import { identityFor } from '../../data/leagues';
import { getOverlayRoot, useOverlayChromeLock } from '../../lib/overlayRoot';
import { EASE_OUT_IOS, SHEET_DISMISS, SPRING_SHEET, prefersReducedMotion, useIsDesktop } from '../../lib/motion';
import { useLeagues } from '../../hooks/useQueries';
import { CyclingDuelDetail } from './CyclingDuelDetail';

/**
 * Cycling duel detail as a slide-up overlay — same dismiss model as LiveFixtureSheet
 * so hub tabs and the live board stay mounted underneath.
 */
export function LiveDuelSheet({
  openDuelId,
  onClose,
  leagueId,
  round,
  fixtures,
  entry
}: {
  openDuelId: string | null;
  onClose: () => void;
  leagueId: string;
  round: Round;
  fixtures: Fixture[];
  entry: Entry | null | undefined;
}) {
  const reduce = prefersReducedMotion();
  const wide = useIsDesktop();
  const [raised, setRaised] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [shownId, setShownId] = useState<string | null>(null);

  useEffect(() => {
    if (openDuelId) {
      setShownId(openDuelId);
      setExiting(false);
    } else if (shownId != null) {
      setExiting(true);
    }
  }, [openDuelId, shownId]);

  const open = !!openDuelId;
  const duelId = openDuelId ?? shownId;
  const fixture = useMemo(
    () => fixtures.find((f) => f.fixtureId === duelId),
    [fixtures, duelId]
  );

  useOverlayChromeLock(open || exiting);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setRaised(false);
      return;
    }
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setRaised(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence
      initial={false}
      onExitComplete={() => {
        setExiting(false);
        setShownId(null);
      }}
    >
      {open && fixture && wide && (
        <motion.div
          key="live-duel-backdrop"
          aria-hidden
          onClick={onClose}
          className="pointer-events-auto fixed inset-0 bg-night/40 backdrop-blur-[2px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: raised ? 1 : 0 }}
          exit={{ opacity: 0, transition: { duration: 0.2, ease: EASE_OUT_IOS } }}
        />
      )}
      {open && fixture && (
        <motion.div
          key="live-duel-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Duel detail"
          className={cn(
            'pointer-events-auto fixed flex flex-col bg-white',
            wide
              ? 'right-0 top-0 bottom-0 w-[min(400px,46vw)] border-l border-black/10 shadow-[-26px_0_70px_-34px_rgba(20,20,50,0.55)] lg:w-[min(520px,96vw)]'
              : 'inset-0 w-full pt-safe'
          )}
          initial={reduce ? { opacity: 0 } : wide ? { x: '100%' } : { y: '100%' }}
          animate={
            reduce
              ? { opacity: raised ? 1 : 0 }
              : wide
                ? { x: raised ? 0 : '100%' }
                : { y: raised ? 0 : '100%' }
          }
          exit={
            reduce
              ? { opacity: 0 }
              : wide
                ? { x: '100%', transition: SHEET_DISMISS }
                : { y: '100%', transition: SHEET_DISMISS }
          }
          transition={reduce ? { duration: 0.18, ease: EASE_OUT_IOS } : SPRING_SHEET}
          style={{ willChange: 'transform' }}
        >
          <div className={cn('flex min-h-0 flex-1 flex-col', wide ? '' : SCREEN_COLUMN)}>
            <div className="native-scroll no-scrollbar min-h-0 flex-1 overflow-y-auto px-[18px] pb-8 pt-2">
              <LiveDuelContent
                fixture={fixture}
                fixtures={fixtures}
                entry={entry ?? null}
                round={round}
                leagueId={leagueId}
                onClose={onClose}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    getOverlayRoot()
  );
}

function LiveDuelContent({
  fixture,
  fixtures,
  entry,
  round,
  leagueId,
  onClose
}: {
  fixture: Fixture;
  fixtures: Fixture[];
  entry: Entry | null;
  round: Round;
  leagueId: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data: leagues } = useLeagues();
  const leagueName =
    leagues?.find((l) => l.leagueId === leagueId)?.name ?? identityFor(leagueId)?.name ?? 'League';

  const goPicks = () => {
    onClose();
    navigate(`/play/${leagueId}?round=${round.roundId}`);
  };
  const goBoard = () => {
    onClose();
    navigate(`/play/${leagueId}/waiting?round=${round.roundId}`);
  };

  return (
    <CyclingDuelDetail
      embedded
      fixture={fixture}
      fixtures={fixtures}
      entry={entry}
      round={round}
      leagueName={leagueName}
      onBack={onClose}
      onPicks={goPicks}
      onBoard={goBoard}
    />
  );
}
